import { getOrgTeamLabel, ORG_TEAMS, teamForJobTitle } from "@shared/constants/org-teams";

export const NO_TEAM_LABEL = "Sem time";
export const NO_JOB_TITLE_LABEL = "Sem cargo";

/** How a question's answers are summarised: a mean, a list of texts, or a count per option. */
export type QuestionKind = "scale" | "text" | "choice";

export function questionKind(questionType: string | null | undefined): QuestionKind {
  if (questionType === "scale") return "scale";
  if (questionType === "single_choice") return "choice";
  return "text"; // short_text, long_text, legacy "text"
}

export type BreakdownQuestion = {
  id: string;
  questionText: string;
  questionType?: string | null;
  questionOrder?: number | null;
};

export type BreakdownResponse = {
  status: string;
  employee?: { jobTitle?: string | null } | null;
  answers?: {
    pulseQuestionInstanceId: string;
    answerValue: number | null;
    answerText?: string | null;
  }[];
};

export type GroupComment = { text: string; jobTitle: string };

export type BreakdownRow = {
  group: string;
  invited: number;
  answered: number;
  /** 0–100, rounded. */
  responseRate: number;
  /** Scale questions: mean of who answered it; null when nobody in the group did. */
  averages: Record<string, number | null>;
  /** Text questions: the non-empty answers of the group. */
  comments: Record<string, GroupComment[]>;
  /** Single-choice questions: how many picked each option. */
  choices: Record<string, Record<string, number>>;
};

export type SurveyBreakdown = {
  /** Ordered questions, or none when `withAnswers` is false. */
  questions: BreakdownQuestion[];
  byTeam: BreakdownRow[];
  byJobTitle: BreakdownRow[];
  /** Everyone in the instance — exact figures, not an average of group means. */
  total: BreakdownRow;
};

const isAnswered = (status: string) => status === "completed" || status === "finished";

export const sortQuestions = <Q extends BreakdownQuestion>(questions: Q[]): Q[] =>
  [...questions].sort((a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0));

/** Team label a response is grouped under (derived from the current cargo). */
export const responseTeam = (response: BreakdownResponse) =>
  getOrgTeamLabel(teamForJobTitle(response.employee?.jobTitle)) ?? NO_TEAM_LABEL;

/** Cargo a response is grouped under. */
export const responseJobTitle = (response: BreakdownResponse) =>
  response.employee?.jobTitle?.trim() || NO_JOB_TITLE_LABEL;

/**
 * Results of one survey instance grouped by time (team, derived from the
 * cargo) and by cargo: participation, and — when the payload carries answer
 * content — the mean of each scale question, the texts of each text question
 * and the count per option of each single-choice question. Uses the
 * respondent's current cargo. Callers that can't read answers get them
 * stripped by the server and must pass `withAnswers: false`: participation
 * only, never figures computed from the few rows a leader may still see.
 */
export function buildSurveyBreakdown(
  responses: BreakdownResponse[],
  questions: BreakdownQuestion[],
  { withAnswers }: { withAnswers: boolean },
): SurveyBreakdown {
  const ordered = withAnswers ? sortQuestions(questions) : [];
  const kinds = new Map(ordered.map((q) => [q.id, questionKind(q.questionType)]));

  const group = (
    keyOf: (r: BreakdownResponse) => string,
    order: (a: string, b: string) => number,
  ): BreakdownRow[] => {
    const acc = new Map<
      string,
      {
        invited: number;
        answered: number;
        sums: Map<string, { total: number; n: number }>;
        comments: Map<string, GroupComment[]>;
        choices: Map<string, Record<string, number>>;
      }
    >();
    for (const response of responses) {
      const key = keyOf(response);
      let g = acc.get(key);
      if (!g) {
        g = { invited: 0, answered: 0, sums: new Map(), comments: new Map(), choices: new Map() };
        acc.set(key, g);
      }
      g.invited++;
      if (!isAnswered(response.status)) continue;
      g.answered++;
      for (const answer of response.answers ?? []) {
        const kind = kinds.get(answer.pulseQuestionInstanceId);
        if (kind === "scale") {
          if (answer.answerValue === null || answer.answerValue === undefined) continue;
          const s = g.sums.get(answer.pulseQuestionInstanceId) ?? { total: 0, n: 0 };
          s.total += answer.answerValue;
          s.n++;
          g.sums.set(answer.pulseQuestionInstanceId, s);
        } else if (kind === "text") {
          const text = answer.answerText?.trim();
          if (!text) continue;
          const list = g.comments.get(answer.pulseQuestionInstanceId) ?? [];
          list.push({ text, jobTitle: responseJobTitle(response) });
          g.comments.set(answer.pulseQuestionInstanceId, list);
        } else if (kind === "choice") {
          const option = answer.answerText?.trim();
          if (!option) continue;
          const counts = g.choices.get(answer.pulseQuestionInstanceId) ?? {};
          counts[option] = (counts[option] ?? 0) + 1;
          g.choices.set(answer.pulseQuestionInstanceId, counts);
        }
      }
    }
    return Array.from(acc.entries())
      .map(([key, g]): BreakdownRow => {
        const averages: BreakdownRow["averages"] = {};
        const comments: BreakdownRow["comments"] = {};
        const choices: BreakdownRow["choices"] = {};
        for (const q of ordered) {
          const kind = kinds.get(q.id);
          if (kind === "scale") {
            const s = g.sums.get(q.id);
            averages[q.id] = s && s.n > 0 ? s.total / s.n : null;
          } else if (kind === "text") {
            comments[q.id] = g.comments.get(q.id) ?? [];
          } else {
            choices[q.id] = g.choices.get(q.id) ?? {};
          }
        }
        return {
          group: key,
          invited: g.invited,
          answered: g.answered,
          responseRate: g.invited > 0 ? Math.round((g.answered / g.invited) * 100) : 0,
          averages,
          comments,
          choices,
        };
      })
      .sort((a, b) => order(a.group, b.group));
  };

  const teamRank = (label: string) => {
    const i = ORG_TEAMS.findIndex((t) => t.label === label);
    return i === -1 ? ORG_TEAMS.length : i;
  };

  const byTeam = group(responseTeam, (a, b) => teamRank(a) - teamRank(b));
  const byJobTitle = group(responseJobTitle, (a, b) => {
    if (a === NO_JOB_TITLE_LABEL) return 1;
    if (b === NO_JOB_TITLE_LABEL) return -1;
    return a.localeCompare(b, "pt-BR");
  });
  const [total] = group(
    () => "Total",
    () => 0,
  );
  const emptyTotal: BreakdownRow = {
    group: "Total",
    invited: 0,
    answered: 0,
    responseRate: 0,
    averages: {},
    comments: {},
    choices: {},
  };

  return { questions: ordered, byTeam, byJobTitle, total: total ?? emptyTotal };
}
